import React, { useEffect, useState } from 'react';
import API from './api';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { MapPin } from 'lucide-react';
import { FaBookOpen } from 'react-icons/fa';
import StudentDetailsModal from './StudentDetailsModal';

// Ikonica za marker
const teacherIcon = new L.Icon({
  iconUrl: 'https://cdn.jsdelivr.net/npm/leaflet@1.7/dist/images/marker-icon.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

function MapModal({ open, onClose, position, label }) {
  if (!open || !position?.lat || !position?.lon) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-lg relative p-4">
        <button
          className="absolute top-2 right-2 text-gray-400 hover:text-red-600 text-xl"
          onClick={onClose}
        >
          ×
        </button>
        <h3 className="text-sm mb-2">{label}</h3>
        <div className="h-80 w-full">
          <MapContainer
            center={[position.lat, position.lon]}
            zoom={15}
            scrollWheelZoom={false}
            className="h-full w-full rounded"
          >
            <TileLayer
              attribution="© OpenStreetMap"
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <Marker position={[position.lat, position.lon]} icon={teacherIcon}>
              <Popup>{label}</Popup>
            </Marker>
          </MapContainer>
        </div>
      </div>
    </div>
  );
}

function addMinutesHHMM(hhmm, mins) {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(':').map(Number);
  const d = new Date(2000, 0, 1, h || 0, m || 0, 0, 0);
  d.setMinutes(d.getMinutes() + (mins || 0));
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

// Grupisanje po datumu
function groupByDate(visits) {
  return visits.reduce((acc, v) => {
    const date = v.scheduled_at.slice(0, 10);
    if (!acc[date]) acc[date] = [];
    acc[date].push(v);
    return acc;
  }, {});
}

// Badge statusa
function StatusBadge({ status, children }) {
  let color = 'bg-gray-200 text-gray-700';
  if (status === 'pending') color = 'bg-yellow-100 text-yellow-800';
  if (status === 'accepted') color = 'bg-green-100 text-green-700';
  if (status === 'rejected') color = 'bg-red-100 text-red-700';
  if (status === 'draft') color = 'bg-blue-100 text-blue-700';
  if (status === 'complete') color = 'bg-green-200 text-green-900';
  if (status === 'canceled') color = 'bg-purple-200 text-purple-700';
  return (
    <span
      className={`inline-block px-2 py-1 rounded text-xs font-semibold ${color}`}
    >
      {children || status}
    </span>
  );
}

// Time + type + EI/PI
function VisitTimeType({ scheduled_at, type, event_end_time, program_type }) {
  const from = scheduled_at?.slice(11, 16);
  const to = (event_end_time && event_end_time.slice(0, 5)) || addMinutesHHMM(from, 45);

  const badgeColor =
    type === 'event'
      ? 'bg-yellow-100 text-orange-800'
      : 'bg-green-100 text-green-800';
  const typeLabel = type === 'event' ? 'Event' : 'Standard';

  const prog =
    program_type === 'PI'
      ? {
          label: 'PI',
          cls: 'bg-orange-100 text-orange-800 border border-orange-300',
        }
      : program_type === 'EI'
      ? {
          label: 'EI',
          cls: 'bg-blue-100 text-blue-800 border border-blue-300',
        }
      : null;

  return (
    <div className="flex flex-col items-end text-right sm:items-start sm:text-left">
      <span className="font-mono text-base">
        {from && to ? `${from}–${to}` : '-'}
      </span>
      <div className="mt-1 flex items-center gap-1">
        <span
          className={`px-2 py-0.5 rounded text-xs font-semibold ${badgeColor}`}
        >
          {typeLabel}
        </span>
        {prog && (
          <span
            className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${prog.cls}`}
          >
            {prog.label}
          </span>
        )}
      </div>
    </div>
  );
}

function AdminVisitsToday() {
  const [visits, setVisits] = useState([]);
  const [serverDateStr, setServerDateStr] = useState(null);
  const [studentDetailsModal, setStudentDetailsModal] = useState({
    open: false,
    student: null,
  });
  const [showNotesHistory, setShowNotesHistory] = useState(false);
  const [notesHistory, setNotesHistory] = useState([]);
  const [notesHistoryLoading, setNotesHistoryLoading] = useState(false);
  const [notesHistoryStudent, setNotesHistoryStudent] = useState(null);
  const [mapModal, setMapModal] = useState({
    open: false,
    position: null,
    label: '',
  });
  const [gsignLog, setGsignLog] = useState({});
  const [gsignMapModal, setGsignMapModal] = useState({
    open: false,
    position: null,
    date: null,
  });

  function round2(num) {
    return Math.round(Number(num) * 100) / 100;
  }

  useEffect(() => {
    API.get('/server-time').then((res) => {
      setServerDateStr(res.data.serverTime.slice(0, 10));
    });
  }, []);

  useEffect(() => {
    if (serverDateStr) fetchTodayVisits(serverDateStr);
    // eslint-disable-next-line
  }, [serverDateStr]);

  const fetchTodayVisits = async (dateStr) => {
    const params = { date_from: dateStr, date_to: dateStr };
    const res = await API.get('/admin/visits', { params });
    const rows = res.data || [];
    setVisits(rows);
    if (rows.length) {
      const ids = rows.map((v) => v.visit_id).filter(Boolean).join(',');
      if (ids) {
        const logs = await API.get('/visit-gsign-log', {
          params: { visit_ids: ids },
        });
        setGsignLog(logs.data || {});
      }
    }
  };

  const grouped = groupByDate(visits);

  const openStudentModal = async (studentId) => {
    try {
      const res = await API.get(`/students/${studentId}/full`);
      setStudentDetailsModal({ open: true, student: res.data });
    } catch {
      alert('Failed to load student details');
    }
  };

  const handleShowTeacherMap = (visit) => {
    if (visit.checkout_lat != null && visit.checkout_lon != null) {
      setMapModal({
        open: true,
        position: {
          lat: Number(visit.checkout_lat),
          lon: Number(visit.checkout_lon),
        },
        label: 'Teacher location (Checkout)',
      });
      return;
    }
    if (visit.checkin_lat != null && visit.checkin_lon != null) {
      setMapModal({
        open: true,
        position: {
          lat: Number(visit.checkin_lat),
          lon: Number(visit.checkin_lon),
        },
        label: 'Teacher location (Check-in)',
      });
      return;
    }
    if (visit.teacher_lat != null && visit.teacher_lon != null) {
      setMapModal({
        open: true,
        position: {
          lat: Number(visit.teacher_lat),
          lon: Number(visit.teacher_lon),
        },
        label: 'Teacher home/base location',
      });
      return;
    }
    alert('Location not available');
  };

  // koristi effective student lokaciju iz /admin/visits
  const handleShowStudentMap = (visit) => {
    if (visit.student_lat != null && visit.student_lon != null) {
      const src =
        visit.student_location_source === 'alt'
          ? 'ALT address'
          : 'Primary address';
      setMapModal({
        open: true,
        position: {
          lat: Number(visit.student_lat),
          lon: Number(visit.student_lon),
        },
        label: `${visit.student_name || 'Student'} (${src})`,
      });
    } else {
      alert('Location not available for this visit');
    }
  };

  const openNotesHistoryModal = async (studentId, studentName) => {
    setNotesHistoryLoading(true);
    setShowNotesHistory(true);
    setNotesHistoryStudent(studentName);
    try {
      const res = await API.get(`/students/${studentId}/notes-history`);
      setNotesHistory(res.data || []);
    } catch {
      setNotesHistory([]);
    } finally {
      setNotesHistoryLoading(false);
    }
  };

  return (
    <div className="w-full px-1 sm:px-1">
      <h2 className="text-lg font-bold mb-4">Today's Visits</h2>
      {!serverDateStr ? (
        <div className="p-4 text-gray-500 text-center">
          Loading server time...
        </div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="text-gray-500 mt-6">No visits found for today.</div>
      ) : (
        Object.entries(grouped).map(([date, group]) => (
          <div
            key={date}
            className="mb-6 shadow rounded-lg bg-white border"
          >
            <div className="font-bold text-base px-4 py-3 bg-gray-50 rounded-t-lg flex items-center">
              <span>{date}</span>
              <span className="ml-3 bg-blue-200 text-blue-900 text-xs px-2 py-0.5 rounded-full">
                {group.length} visits
              </span>
            </div>

            {/* DESKTOP */}
            <div className="overflow-x-auto hidden sm:block">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-2 py-1">Time</th>
                    <th className="px-2 py-1">Student</th>
                    <th className="px-2 py-1">Teacher</th>
                    <th className="px-2 py-1">Status</th>
                    <th className="px-2 py-1">Reassigned</th>
                    <th className="px-2 py-1">G-sign</th>
                  </tr>
                </thead>
                <tbody>
                  {group.map((v) => (
                    <tr
                      key={v.visit_id}
                      className="hover:bg-gray-50 cursor-pointer"
                    >
                      <td className="px-2 py-1">
                        <VisitTimeType
                          scheduled_at={v.scheduled_at}
                          type={v.type}
                          event_end_time={v.event_end_time}
                          program_type={v.program_type}
                        />
                      </td>

                      {/* Student */}
                      <td className="px-2 py-1">
                        <span
                          onClick={() => openStudentModal(v.student_id)}
                          className="text-blue-700 underline cursor-pointer hover:text-blue-900"
                          title="Student Details"
                        >
                          {v.student_name}
                        </span>
                        <button
                          type="button"
                          title="Show student location"
                          className="text-blue-600 hover:text-blue-900 p-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShowStudentMap(v);
                          }}
                        >
                          <MapPin size={16} />
                        </button>
                        <button
                          type="button"
                          title="Show Notes History"
                          className="text-purple-700 hover:text-purple-900 p-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            openNotesHistoryModal(
                              v.student_id,
                              v.student_name
                            );
                          }}
                        >
                          <FaBookOpen size={16} />
                        </button>
                      </td>

                      {/* Teacher */}
                      <td className="px-2 py-1">
                        <span className="inline-flex items-center gap-1">
                          {v.teacher_name}
                          <button
                            type="button"
                            title="Show teacher location"
                            className="text-blue-600 hover:text-blue-900 p-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShowTeacherMap(v);
                            }}
                          >
                            <MapPin size={16} />
                          </button>
                        </span>
                        {v.teacher_id !== v.original_teacher_id &&
                          v.original_teacher_name && (
                            <div className="text-xs text-gray-400 mt-1">
                              Default: {v.original_teacher_name}
                            </div>
                          )}
                      </td>

                      <td className="px-2 py-1 capitalize">
                        <StatusBadge status={v.status}>
                          {v.status}
                        </StatusBadge>
                        {v.status === 'canceled' && v.notes && (
                          <div className="text-xs text-purple-700 mt-1 italic">
                            {v.notes}
                          </div>
                        )}
                      </td>

                      <td className="px-2 py-1 text-xs text-gray-400">
                        {v.is_reassigned ? (
                          <StatusBadge status={v.reassigned_status}>
                            {v.reassigned_status}
                          </StatusBadge>
                        ) : (
                          'Default Teacher'
                        )}
                      </td>

                      <td className="px-2 py-1 text-center">
                        {v.g_sign_confirmed ? (() => {
                          const gsign = gsignLog[v.visit_id];
                          const hasGsign =
                            gsign &&
                            gsign.lat != null &&
                            gsign.lon != null;
                          const gLat2 = hasGsign
                            ? round2(gsign.lat)
                            : null;
                          const gLon2 = hasGsign
                            ? round2(gsign.lon)
                            : null;
                          const checkinLat2 =
                            v.checkin_lat != null
                              ? round2(v.checkin_lat)
                              : null;
                          const checkinLon2 =
                            v.checkin_lon != null
                              ? round2(v.checkin_lon)
                              : null;
                          const checkoutLat2 =
                            v.checkout_lat != null
                              ? round2(v.checkout_lat)
                              : null;
                          const checkoutLon2 =
                            v.checkout_lon != null
                              ? round2(v.checkout_lon)
                              : null;

                          let color = 'text-gray-400';
                          if (hasGsign) {
                            const checkinMatch =
                              checkinLat2 != null &&
                              checkinLon2 != null &&
                              gLat2 === checkinLat2 &&
                              gLon2 === checkinLon2;
                            const checkoutMatch =
                              checkoutLat2 != null &&
                              checkoutLon2 != null &&
                              gLat2 === checkoutLat2 &&
                              gLon2 === checkoutLon2;
                            if (checkinMatch || checkoutMatch) {
                              color = 'text-green-700';
                            } else {
                              color = 'text-red-600';
                            }
                          }

                          return (
                            <div className="flex flex-col items-center">
                              <div className="flex flex-row items-center gap-1">
                                <span className="text-green-600 text-md">
                                  ✅
                                </span>
                                {hasGsign && (
                                  <button
                                    title="Show confirmation location"
                                    className={color}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setGsignMapModal({
                                        open: true,
                                        position: {
                                          lat: Number(gsign.lat),
                                          lon: Number(gsign.lon),
                                        },
                                        date: gsign.confirmed_at,
                                      });
                                    }}
                                  >
                                    <MapPin size={16} />
                                  </button>
                                )}
                              </div>
                              {gsign && (
                                <span
                                  className={`text-xs mt-0.5 ${color}`}
                                >
                                  {gsign.confirmed_at
                                    ?.slice(0, 16)
                                    .replace('T', ' ')}
                                </span>
                              )}
                            </div>
                          );
                        })() : (
                          <span className="text-red-600 text-md">
                            ❌
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* MOBILNI KARTICE */}
            <div className="sm:hidden flex flex-col gap-2 p-2">
              {group.map((v) => {
                let rowClass =
                  'rounded-2xl border shadow p-3 mb-1 bg-white';
                return (
                  <div key={v.visit_id} className={rowClass}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 flex items-center gap-2">
                        <span
                          onClick={() => openStudentModal(v.student_id)}
                          className="text-blue-700 underline cursor-pointer hover:text-blue-900"
                        >
                          {v.student_name}
                        </span>
                        <button
                          onClick={() => handleShowStudentMap(v)}
                          className="ml-1 text-gray-400 hover:text-blue-600"
                          title="Show student location"
                        >
                          <MapPin size={16} />
                        </button>
                        <button
                          onClick={() =>
                            openNotesHistoryModal(
                              v.student_id,
                              v.student_name
                            )
                          }
                          className="ml-1 text-purple-700 hover:text-purple-900"
                          title="Show Notes History"
                        >
                          <FaBookOpen size={16} />
                        </button>
                      </div>
                      <div className="flex-1 text-right">
                        <VisitTimeType
                          scheduled_at={v.scheduled_at}
                          type={v.type}
                          event_end_time={v.event_end_time}
                          program_type={v.program_type}
                        />
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 items-center mt-2">
                      <StatusBadge status={v.status}>
                        {v.status}
                      </StatusBadge>
                      {v.status === 'canceled' && v.notes && (
                        <span className="text-xs text-purple-700 italic">
                          {v.notes}
                        </span>
                      )}
                      {v.teacher_id !== v.original_teacher_id &&
                        v.reassigned_status && (
                          <span
                            className={`text-xs ${
                              v.reassigned_status === 'rejected'
                                ? 'text-red-700'
                                : 'text-orange-700'
                            }`}
                          >
                            Substitute: {v.reassigned_status}
                          </span>
                        )}
                    </div>

                    <div className="grid grid-cols-2 gap-2 mt-3">
                      <div>
                        <span className="block text-xs text-gray-600">
                          Teacher
                        </span>
                        <span>{v.teacher_name}</span>
                        {v.teacher_id !==
                          v.original_teacher_id &&
                          v.original_teacher_name && (
                            <div className="text-xs text-gray-400 mt-1">
                              Default: {v.original_teacher_name}
                            </div>
                          )}
                        <button
                          onClick={() => handleShowTeacherMap(v)}
                          className="ml-1 text-gray-400 hover:text-blue-600"
                          title="Show teacher location"
                        >
                          <MapPin size={16} />
                        </button>
                      </div>

                      <div>
                        <span className="block text-xs text-gray-600">
                          G-sign
                        </span>
                        {v.g_sign_confirmed ? (() => {
                          const gsign = gsignLog[v.visit_id];
                          const hasGsign =
                            gsign &&
                            gsign.lat != null &&
                            gsign.lon != null;
                          const gLat2 = hasGsign
                            ? round2(gsign.lat)
                            : null;
                          const gLon2 = hasGsign
                            ? round2(gsign.lon)
                            : null;
                          const checkinLat2 =
                            v.checkin_lat != null
                              ? round2(v.checkin_lat)
                              : null;
                          const checkinLon2 =
                            v.checkin_lon != null
                              ? round2(v.checkin_lon)
                              : null;
                          const checkoutLat2 =
                            v.checkout_lat != null
                              ? round2(v.checkout_lat)
                              : null;
                          const checkoutLon2 =
                            v.checkout_lon != null
                              ? round2(v.checkout_lon)
                              : null;

                          let color = 'text-gray-400';
                          if (hasGsign) {
                            const checkinMatch =
                              checkinLat2 != null &&
                              checkinLon2 != null &&
                              gLat2 === checkinLat2 &&
                              gLon2 === checkinLon2;
                            const checkoutMatch =
                              checkoutLat2 != null &&
                              checkoutLon2 != null &&
                              gLat2 === checkoutLat2 &&
                              gLon2 === checkoutLon2;
                            if (checkinMatch || checkoutMatch) {
                              color = 'text-green-700';
                            } else {
                              color = 'text-red-600';
                            }
                          }
                          return (
                            <div className="flex flex-row items-center gap-1">
                              <span className="text-green-600 text-md">
                                ✅
                              </span>
                              {hasGsign && (
                                <button
                                  title="Show confirmation location"
                                  className={color}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setGsignMapModal({
                                      open: true,
                                      position: {
                                        lat: Number(gsign.lat),
                                        lon: Number(gsign.lon),
                                      },
                                      date: gsign.confirmed_at,
                                    });
                                  }}
                                >
                                  <MapPin size={16} />
                                </button>
                              )}
                            </div>
                          );
                        })() : (
                          <span className="text-red-600 text-md">
                            ❌
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}

      {/* MODALI */}
      <MapModal
        open={mapModal.open}
        onClose={() =>
          setMapModal((m) => ({ ...m, open: false }))
        }
        position={mapModal.position}
        label={mapModal.label}
      />

      <MapModal
        open={gsignMapModal.open}
        onClose={() =>
          setGsignMapModal({
            open: false,
            position: null,
            date: null,
          })
        }
        position={gsignMapModal.position}
        label={`Parent confirmed location${
          gsignMapModal.date
            ? ` at ${gsignMapModal.date
                .slice(0, 16)
                .replace('T', ' ')}`
            : ''
        }`}
      />

      <StudentDetailsModal
        open={studentDetailsModal.open}
        onClose={() =>
          setStudentDetailsModal({
            open: false,
            student: null,
          })
        }
        student={studentDetailsModal.student}
      />

      {/* Notes history modal */}
      {showNotesHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-5 rounded shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-bold">
                Notes History: {notesHistoryStudent}
              </h3>
              <button
                className="text-gray-500"
                onClick={() => setShowNotesHistory(false)}
              >
                ✕
              </button>
            </div>
            {notesHistoryLoading ? (
              <div className="p-4 text-center text-gray-400">
                Loading...
              </div>
            ) : notesHistory.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                No notes found for this student.
              </div>
            ) : (
              <div className="space-y-4">
                {notesHistory.map((entry, idx) => (
                  <div
                    key={idx}
                    className="border rounded p-3 bg-gray-50"
                  >
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-semibold text-blue-900">
                        {entry.teacher_name}
                      </span>
                      <span className="text-xs text-gray-500">
                        {entry.scheduled_at
                          .slice(0, 16)
                          .replace('T', ' ')}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                      {[
                        { key: 'notes_cognitive', label: 'Cognitive' },
                        { key: 'notes_language', label: 'Language' },
                        { key: 'notes_gross_motor', label: 'Gross Motor' },
                        { key: 'notes_fine_motor', label: 'Fine Motor' },
                        { key: 'notes_social', label: 'Social' },
                        { key: 'notes_self_help', label: 'Self Help' },
                      ].map((dom) =>
                        entry[dom.key] &&
                        entry[dom.key].trim() !== '' ? (
                          <div key={dom.key}>
                            <span className="font-semibold">
                              {dom.label}:
                            </span>{' '}
                            <span>{entry[dom.key]}</span>
                          </div>
                        ) : null
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminVisitsToday;



